/**
 * <collection-filters> - opgraderer main-collections no-JS sort/filter-forms til
 * Section Rendering uden reload (THEME-SPEC 3, BUILD_PLAN 2.4). Fjerde forbruger af
 * @theme/section-render.
 *
 * Ved filter/sort-skift (change/submit), pagination-klik og Clear: byg URL'en fra
 * de LEVENDE kontroller (afkrydsede facetter + pris-felter + sort-select), pushState,
 * hent main-collection server-renderet (?section_id=&params), og byt resultaterne ind.
 * Vi laeser live DOM - ikke 3.2-formernes skjulte kryds-inputs - fordi de kun er
 * friske ved fuld page-render (no-JS); efter et ajax-skift ville de vaere foraeldede.
 * Penge/locale forbliver server-ejet - grid'et kommer server-renderet retur. No-JS:
 * 3.2-formerne reloader (uaendret).
 *
 * Facetter re-renderes ved filter-skift (taellinger/tilgaengelighed opdateres), med
 * bevaring af aabne grupper + fokus paa den aendrede kontrol. Det samme gaelder
 * toolbarens to dropdown-facetter ([data-toolbar-filters]): den aabne dropdown og
 * det klikkede punkt genskabes efter swappen, saa multi-valg kan fortsaette uden at
 * panelet lukker under fingeren. Escape lukker den aabne dropdown (fokus tilbage
 * paa summary), og et klik udenfor lukker den.
 *
 * Mobil: filtrene ligger i en <dialog> der aabnes som drawer (showModal -> native focus-trap/Escape); paa
 * desktop vises samme <dialog> inline som sidebar via CSS. popstate gen-renderer fra URL.
 *
 * Sorteringen har TO kontroller (2026-09-28): toolbarens <select> (synlig >= 834px) og en
 * radio-gruppe ved navn sort_by i filter-sheet'en (synlig < 834px, hvor toolbarens form er
 * skjult). Select'en er fortsat #buildUrl's eneste kilde - #syncSort spejler et valg fra den
 * ene kontrol over i den anden, saa der kun findes én sandhed om sorteringen uanset hvilken
 * shopperen roerte, og et viewport-skift aldrig afsloerer en foraeldet vaerdi.
 */
import { fetchSection, debounce, abortable, latestBusy } from '@theme/section-render';

const SECTION = '[id^="shopify-section-"]';
// CUSTOM: r02-l8 - eksplicit render-status saa popstate kan skelne "afbrudt" fra "aegte fejl".
const RENDER = { OK: 'ok', ABORTED: 'aborted', ERROR: 'error' };

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.section = this.closest(SECTION);
    this.results = this.querySelector('[data-collection-results]');
    this.count = this.querySelector('[data-collection-count]');
    this.dialog = this.querySelector('[data-filters-dialog]');
    this.filterForm = this.querySelector('[data-filter-form]');
    this.sortForm = this.querySelector('[data-sort-form]');
    this.opener = null;
    this.nextSignal = abortable();
    // CUSTOM: r02-m2 - kun det seneste kald rydder busy (aria-busy staar til seneste svar lander).
    this.markBusy = latestBusy((busy) => this.toggleAttribute('aria-busy', busy));
    // Pris: debounce baade URL-opdatering og render, saa der ikke spammes history.
    this.applyDebounced = debounce(() => this.#navigate(this.#buildUrl(), true), 350);

    if (this.dialog) {
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.#closeDrawer();
      });
      this.dialog.addEventListener('close', () => {
        this.#setToggle(false);
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('keydown', this.#onKeydown);
    // Klik udenfor den aabne toolbar-dropdown lukker den (native <details> goer det ikke
    // selv). Mutual exclusion mellem de to dropdowns klares af <details name> paa moderne
    // motorer; paa aeldre (Firefox < 130) lukker denne pointerdown-handler alligevel den
    // anden, fordi et summary-klik ligger udenfor den aabne dropdown.
    this.onDocumentPointerDown = (event) => {
      const el = event.target instanceof Element ? event.target : null;
      const inside = el && this.contains(el) && el.closest('[data-toolbar-filters] details[open]');
      if (!inside) this.#closeDropdowns();
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    // Safari fokuserer ikke en <summary> ved museklik, saa Escape ville ellers ikke
    // kunne lukke panelet for en musebruger der. Dokument-fallback, men KUN naar
    // filter-dialogen er lukket - modalen skal beholde sin egen Escape.
    this.onDocumentKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (this.dialog && this.dialog.open) return;
      this.#closeDropdowns();
    };
    document.addEventListener('keydown', this.onDocumentKeydown);
    // CUSTOM: r02-l8 - ved AEGTE render-fejl paa Back/Forward reconciler vi URL<->DOM med en
    // reload; afbrudt (nyere popstate) reloader IKKE.
    this.onPopstate = () => {
      this.#render(window.location.href, true).then((status) => {
        if (status === RENDER.ERROR) window.location.reload();
      });
    };
    window.addEventListener('popstate', this.onPopstate);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopstate);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  get #sectionId() {
    return this.section ? this.section.id.replace('shopify-section-', '') : null;
  }

  // ----- Mobil drawer -----
  #onClick = (event) => {
    const toggle = event.target.closest('[data-filter-toggle]');
    if (toggle) {
      event.preventDefault();
      this.#openDrawer(toggle);
      return;
    }
    if (event.target.closest('[data-filter-close]')) {
      this.#closeDrawer();
      return;
    }
    const clear = event.target.closest('[data-filter-clear]');
    if (clear) {
      event.preventDefault();
      this.#navigate(clear.href, true);
      return;
    }
    // Toolbar-dropdownenes facet-punkter (KCA 3.2): rigtige facet-links
    // (url_to_add/url_to_remove, no-JS korrekte) - opgraderes her til Section
    // Rendering uden reload. Dropdownen bliver staaende aaben (multi-valg).
    const option = event.target.closest('a[data-filter-option]');
    if (option) {
      event.preventDefault();
      this.#navigate(option.href, true);
      return;
    }
    const page = event.target.closest('[data-pagination] a[href]');
    if (page) {
      event.preventDefault();
      // CUSTOM: r02-l7 - scroll FOERST naar den nye side faktisk er renderet (og ikke ved fejl).
      this.#navigate(page.href, false).then((ok) => {
        if (ok) this.#scrollToResults();
      });
    }
  };

  #openDrawer(opener) {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    this.#setToggle(true);
  }
  #closeDrawer() {
    if (this.dialog && this.dialog.open) this.dialog.close();
  }
  #setToggle(expanded) {
    document.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(expanded));
    });
  }

  // ----- Toolbar-dropdowns -----
  #openDropdowns() {
    return [...this.querySelectorAll('[data-toolbar-filters] details[open]')];
  }
  #closeDropdowns() {
    this.#openDropdowns().forEach((details) => {
      details.open = false;
    });
  }
  // Escape lukker KUN naar fokus staar inde i den aabne dropdown - ellers ville den
  // stjaele Escape fra filter-dialogen (som er modal og lukker sig selv).
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const el = event.target instanceof Element ? event.target : null;
    const open = el && el.closest('[data-toolbar-filters] details[open]');
    if (!open) return;
    event.stopPropagation();
    open.open = false;
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  };

  // ----- Ajax sort/filter -----
  #onChange = (event) => {
    const target = event.target;
    if (target.type === 'number') return; // pris: input + debounce
    // Sheet'ens sort-radioer bor inde i filter-formen, men er en SORT-rute: de
    // aendrer ikke facetternes counts, saa der swappes ingen facetter (et swap af
    // [data-filters-list] ville desuden rykke sheet'ens scroll under fingeren).
    // Testen staar FOER facet-ruten, ellers havde #buildUrl laest en foraeldet
    // select og sendt den gamle sortering med.
    if (target.name === 'sort_by' && target.closest('[data-filter-form]')) {
      this.#syncSort(target.value);
      this.#navigate(this.#buildUrl(), false);
      return;
    }
    if (target.closest('[data-filter-form]')) this.#navigate(this.#buildUrl(), true);
    else if (target.closest('[data-sort-form]')) {
      // Sort-formen swappes IKKE paa denne rute (select'en er kilden), saa det
      // synlige spejl skal foelge med i haanden. Teksten kopieres fra den option
      // serveren renderede - der opfindes ingen streng klientside.
      this.#syncSort(target.value);
      this.#navigate(this.#buildUrl(), false);
    }
  };
  #onInput = (event) => {
    if (event.target.type === 'number' && event.target.closest('[data-filter-form]')) {
      this.applyDebounced();
    }
  };
  #onSubmit = (event) => {
    const form = event.target;
    if (form === this.filterForm) {
      event.preventDefault();
      this.#navigate(this.#buildUrl(), true);
      this.#closeDrawer();
    } else if (form === this.sortForm) {
      event.preventDefault();
      this.#navigate(this.#buildUrl(), false);
    }
  };

  // Spejl sorteringen paa tvaers af de to kontroller: toolbarens select (>= 834px) og
  // sheet'ens radio-gruppe (< 834px). Select'en opdateres altid, fordi #buildUrl laeser
  // den; radioerne foelger med, saa et no-JS submit eller et skift af viewport-bredde
  // aldrig viser en anden sortering end URL'en.
  #syncSort(value) {
    const select = this.sortForm && this.sortForm.querySelector('[name="sort_by"]');
    if (select) {
      select.value = value;
      this.#syncSortLabel(select);
    }
    if (!this.filterForm) return;
    this.filterForm.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
      radio.checked = radio.value === value;
    });
  }

  // Spejlet <span> baerer den synlige sorterings-tekst (select'en ligger usynligt
  // ovenpaa, saa kontrollen kan vaere indholdsbred i stedet for saa bred som den
  // laengste option). Holdes i sync naar select'en aendres.
  #syncSortLabel(select) {
    if (!select || select.name !== 'sort_by') return;
    const chosen = select.selectedOptions && select.selectedOptions[0];
    if (!chosen) return;
    const mirror = this.querySelector('[data-sort-value]');
    if (mirror) mirror.textContent = chosen.text;
    // Sheet-gruppens summary viser den aktive sortering, fordi gruppen er lukket som
    // default. Den er server-renderet og swappes IKKE paa sort-ruten (withFacets=false),
    // saa uden dette stod den gamle sortering tilbage naar gruppen blev foldet sammen.
    // Placeholder-optionens tekst ER "Sort by" - den hoerer til i toolbarens spejl, men
    // ikke her: tom sortering = tom vaerdi (:empty skjuler noden).
    const summary = this.querySelector('[data-sort-summary]');
    if (summary) summary.textContent = select.value ? chosen.text : '';
  }

  // Byg URL fra LEVENDE kontroller: facetter (afkrydsede + pris) fra filter-formen
  // (minus dens skjulte sort_by), sort fra select'en. page udelades -> side 1.
  #buildUrl() {
    const params = new URLSearchParams();
    if (this.filterForm) {
      for (const [key, value] of new FormData(this.filterForm)) {
        if (key === 'sort_by') continue;
        if (value !== '') params.append(key, value);
      }
    }
    const sortSelect = this.sortForm && this.sortForm.querySelector('[name="sort_by"]');
    if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);

    const url = new URL(window.location.pathname, window.location.origin);
    url.search = params.toString();
    return url.href;
  }

  async #navigate(href, withFacets) {
    // Render foerst, pushState kun ved succes - saa URL'en aldrig kommer foran DOM.
    // Ingen tavs desync hvis serveren fejler; en reload paa den gamle URL er konsistent.
    const status = await this.#render(href, withFacets); // CUSTOM: r02-l7/l8
    if (status === RENDER.OK) {
      window.history.pushState({}, '', href);
      return true;
    }
    return false;
  }

  async #render(href, withFacets) {
    const sectionId = this.#sectionId;
    if (!sectionId) return RENDER.ABORTED; // CUSTOM: r02-l8 - intet at rendere: hverken push eller reload
    const url = new URL(href, window.location.origin);
    const doneBusy = this.markBusy(); // CUSTOM: r02-m2
    try {
      const html = await fetchSection(sectionId, {
        url: url.pathname,
        params: url.searchParams,
        signal: this.nextSignal(),
      });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Resultater + antal (antal-noden er aria-live -> annonceres ved tekstskift).
      this.#swap(this.results, doc, '[data-collection-results]');
      this.#swap(this.count, doc, '[data-collection-count]');
      // Toolbar-dropdowns: aktiv-tilstand + counts skal foelge facetterne (KCA 3.2),
      // saa de swappes ved HVER render - ogsaa ved sort.
      // Begge swaps genskaber fokus, men hver er hegnet ind om SIT eget subtrae:
      // #swapToolbarFilters roerer kun fokus naar activeElement ligger i toolbaren
      // (host.contains), og #swapFacets kun naar den har et [name] at gaa efter -
      // som toolbarens <a>-punkter aldrig har. De kan altsaa ikke stjaele fokus fra
      // hinanden. Giv ALDRIG toolbar-punkterne et name-attribut uden at rette her.
      if (withFacets) this.#swapFacets(doc);
      this.#swapToolbarFilters(doc);
      // "Ryd alt" har sine egne slots - de skal med, ellers blev den staaende efter
      // en ryd-alt (eller udeblev efter det foerste valg).
      this.#swapToolbarClear(doc);
      // Sort-formen swappes KUN paa facet-ruter (filter-skift og "ryd alt"), aldrig paa
      // selve sort-skiftet - der er select'en jo kilden. Uden dette blev "ryd alt" ved
      // med at vise den gamle sortering i kontrollen, selvom URL'en havde droppet den.
      if (withFacets) this.#swap(this.sortForm, doc, '[data-sort-form]');
      return RENDER.OK; // CUSTOM: r02-l8
    } catch (error) {
      // Afbrudt af et nyere skift: ikke en fejl. Aegte fejl: behold forrige
      // resultater + annoncér (URL er uaendret, saa intet desync).
      if (error.name === 'AbortError') return RENDER.ABORTED; // CUSTOM: r02-l8 - supersedet, ikke en fejl
      this.#announceError();
      return RENDER.ERROR; // CUSTOM: r02-l8
    } finally {
      doneBusy(); // CUSTOM: r02-m2
    }
  }

  #announceError() {
    const status = this.querySelector('[data-collection-status]');
    const message = this.dataset.textError;
    if (!status || !message) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  // To slots, ét indhold: "ryd alt" bor paa ANTAL-raekken under 834px og til hoejre
  // for "flere filtre" derover (CSS viser én ad gangen). Serveren renderer samme
  // markup i begge, saa den FOERSTE i svaret er kilde for dem alle - ellers ville
  // den skjulte slot blive stale og dukke op forkert ved et viewport-skift.
  #swapToolbarClear(doc) {
    const source = doc.querySelector('[data-toolbar-clear]');
    if (!source) return;
    this.querySelectorAll('[data-toolbar-clear]').forEach((slot) => {
      slot.replaceChildren(...source.cloneNode(true).childNodes);
    });
  }

  #swap(target, doc, selector) {
    if (!target) return;
    const source = doc.querySelector(selector);
    if (source) target.replaceChildren(...source.childNodes);
  }

  // Re-render facet-listen, men bevar aabne grupper + fokus paa den aendrede kontrol.
  #swapFacets(doc) {
    const list = this.querySelector('[data-filters-list]');
    const source = doc.querySelector('[data-filters-list]');
    if (!list || !source) return;
    const openGroups = [...list.querySelectorAll('details[open]')].map((d) => d.dataset.group);
    const active = document.activeElement;
    const name = active && active.name;
    const value = active && active.value;
    /* === CUSTOM: r02-c7 === */
    // Redigeres et pris-felt netop nu? Behold brugerens igangvaerende vaerdi + caret,
    // saa server-vaerdien ikke stomper den halvtastede pris (og fokus ikke falder til body).
    const editingPrice = !!(active && active.type === 'number' && list.contains(active));
    const priceName = editingPrice ? active.name : null;
    const priceValue = editingPrice ? active.value : null;
    // selectionStart/End kaster InvalidStateError paa <input type=number> i WebKit
    // (og aeldre Chrome) - laes defensivt. Caret-bevaring er best-effort; det vigtige
    // er at vaerdi + fokus bevares, saa svaret ikke stomper den halvtastede pris.
    let selStart = null;
    let selEnd = null;
    if (editingPrice) {
      try {
        selStart = active.selectionStart;
        selEnd = active.selectionEnd;
      } catch {
        /* number-input uden selection-API: spring caret-bevaring over */
      }
    }
    /* === END CUSTOM: r02-c7 === */

    list.replaceChildren(...source.childNodes);

    list.querySelectorAll('details').forEach((d) => {
      if (openGroups.includes(d.dataset.group)) d.open = true;
    });
    /* === CUSTOM: r02-c7 === */
    if (priceName != null) {
      const input = list.querySelector(`[name="${CSS.escape(priceName)}"]`);
      if (input) {
        input.value = priceValue; // brugerens vaerdi vinder over server-vaerdien
        input.focus();
        try {
          input.setSelectionRange(selStart, selEnd); // caret bevares (kan kaste paa nogle input-typer)
        } catch {
          /* setSelectionRange understoettes ikke paa alle input-typer - ignorer */
        }
      }
      return;
    }
    /* === END CUSTOM: r02-c7 === */
    if (name) {
      const selector = value
        ? `[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
        : `[name="${CSS.escape(name)}"]`;
      const restored = list.querySelector(selector);
      if (restored) restored.focus();
    }
  }

  // Toolbar-dropdowns: byt indhold, men bevar den aabne dropdown og fokus paa det
  // punkt der lige blev klikket - ellers lukkede panelet ved hvert multi-valg og
  // fokus faldt til <body>.
  #swapToolbarFilters(doc) {
    const host = this.querySelector('[data-toolbar-filters]');
    const source = doc.querySelector('[data-toolbar-filters]');
    if (!host || !source) return;

    const openKeys = this.#openDropdowns().map((details) => details.dataset.toolbarFilter);
    const active = document.activeElement;
    const inHost = active instanceof Element && host.contains(active);
    const group = inHost ? active.closest('details[data-toolbar-filter]') : null;
    const groupKey = group ? group.dataset.toolbarFilter : null;
    const option = inHost ? active.closest('[data-filter-value]') : null;
    const optionValue = option ? option.dataset.filterValue : null;

    host.replaceChildren(...source.childNodes);

    host.querySelectorAll('details[data-toolbar-filter]').forEach((details) => {
      if (openKeys.includes(details.dataset.toolbarFilter)) details.open = true;
    });

    if (!groupKey) return;
    const restoredGroup = host.querySelector(`details[data-toolbar-filter="${CSS.escape(groupKey)}"]`);
    if (!restoredGroup) return;
    // Et punkt kan vaere faldet til 0 traef og renderes da som <span> (ikke fokuserbart)
    // - saa lander fokus paa dropdownens summary i stedet for paa <body>.
    const restored =
      (optionValue && restoredGroup.querySelector(`a[data-filter-value="${CSS.escape(optionValue)}"]`)) ||
      restoredGroup.querySelector('summary');
    if (restored) restored.focus();
  }

  #scrollToResults() {
    if (!this.results) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.results.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
